import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Конфигурация конерктного модуля
export interface modulesConfigElem {
	moduleName: string;
	moduleCategory: string;
	moduleWeight: number;
	moduleOrder: number;
}
// Конфигурация конкретной секции
export interface sectionsConfigElem {
	sectionName: string;
	sectionWeight: number;
}

/* 
	Объект конфигурации интерфейса
	Weight - "вес" модулей/секций для расчета их начальной ширины при загрузке страницы.
	Если вес === 0, тогда скрываем элемент
	Order - порядок расположения модулей/секций. 1 - минимальный, 3 - максимальный
*/
export type HighlightedAppSelection = {
	appId: string;
	accounts: string[];
};

function unionHighlightedAccounts(
	entries: HighlightedAppSelection[],
): string[] {
	const set = new Set<string>();
	for (const { accounts } of entries) {
		for (const id of accounts) {
			set.add(id);
		}
	}
	return Array.from(set);
}

export interface AppState {
	isSocketLoaded: boolean;
	socketStatus:
		| "online"
		| "connecting"
		| "offline-reconnect"
		| "connection-error"
		| "auth-error";
	sectionsConfig: sectionsConfigElem[]; // Конфиг секций Chat/Dashboard
	modulesConfig: modulesConfigElem[]; // Конфиг модулей внутри секций
	highlightedApps: HighlightedAppSelection[];
	highlightedAccounts: string[];
	// При явном сбросе раскладки увеличивается — remount SplitPane без смены состава панелей
	layoutHardResetNonce: number;
	// Только вертикальный сплит workspace (dashboard | chat): remount без reset горизонтальных SplitPane внутри секций
	workspaceSplitResetNonce: number;
}

// Дефолтные конфиги для сброса (экспортируем для использования в InterfaceSwitcher)
export const startModulesConfig: modulesConfigElem[] = [
	{
		moduleName: "accounts",
		moduleCategory: "dashboard",
		moduleWeight: 1,
		moduleOrder: 1,
	},
	{
		moduleName: "campaigns",
		moduleCategory: "dashboard",
		moduleWeight: 2,
		moduleOrder: 2,
	},
	{
		moduleName: "apps",
		moduleCategory: "dashboard",
		moduleWeight: 1,
		moduleOrder: 3,
	},
	{
		moduleName: "chat_history",
		moduleCategory: "chat",
		moduleWeight: 1,
		moduleOrder: 1,
	},
	{
		moduleName: "chat_window",
		moduleCategory: "chat",
		moduleWeight: 2,
		moduleOrder: 2,
	},
	{
		moduleName: "chat_form",
		moduleCategory: "chat",
		moduleWeight: 1,
		moduleOrder: 3,
	},
];
export const startSectionsConfig: sectionsConfigElem[] = [
	{
		sectionName: "dashboard",
		sectionWeight: 1,
	},
	{
		sectionName: "chat",
		sectionWeight: 1,
	},
];

/*
	Сортирует modulesConfig по дефолтному порядку
	сначала по категории (порядок из startSectionsConfig),
	затем по moduleOrder внутри категории.
	Модули, отсутствующие в startModulesConfig, уходят в конец.
*/
export const applyDefaultOrder = (
	modules: modulesConfigElem[],
): modulesConfigElem[] => {
	const categoryOrder = startSectionsConfig.map((s) => s.sectionName);
	return [...modules].sort((a, b) => {
		const catA = categoryOrder.indexOf(a.moduleCategory);
		const catB = categoryOrder.indexOf(b.moduleCategory);
		const catPosA = catA === -1 ? Infinity : catA;
		const catPosB = catB === -1 ? Infinity : catB;
		if (catPosA !== catPosB) return catPosA - catPosB;
		return a.moduleOrder - b.moduleOrder;
	});
};

const initialState: AppState = {
	isSocketLoaded: false,
	socketStatus: "connecting",
	highlightedApps: [],
	highlightedAccounts: [],
	sectionsConfig: [],
	modulesConfig: [],
	layoutHardResetNonce: 0,
	workspaceSplitResetNonce: 0,
};

const appSlice = createSlice({
	name: "app",
	initialState,
	reducers: {
		bumpLayoutHardResetNonce(state) {
			state.layoutHardResetNonce += 1;
		},
		setIsSocketLoaded(state, action: PayloadAction<boolean>) {
			state.isSocketLoaded = action.payload;
		},
		toggleHighlightedApp(
			state,
			action: PayloadAction<{ appId: string; accounts: string[] }>,
		) {
			const { appId, accounts } = action.payload;
			const idx = state.highlightedApps.findIndex((h) => h.appId === appId);
			if (idx !== -1) {
				state.highlightedApps.splice(idx, 1);
			} else {
				state.highlightedApps.push({ appId, accounts });
			}
			state.highlightedAccounts = unionHighlightedAccounts(
				state.highlightedApps,
			);
		},
		clearHighlightedApps(state) {
			state.highlightedApps = [];
			state.highlightedAccounts = [];
		},
		setModulesConfiguration(state, action: PayloadAction<number>) {
			state.modulesConfig = startModulesConfig; // Временное решение
		},
		resetConfiguration(state) {
			const ordered = applyDefaultOrder(startModulesConfig);
			state.modulesConfig = ordered;
			state.sectionsConfig = startSectionsConfig;
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem("modulesConfig", JSON.stringify(ordered));
					localStorage.setItem(
						"sectionsConfig",
						JSON.stringify(startSectionsConfig),
					);
				} catch (err) {
					console.warn("Failed to reset configs in localStorage", err);
				}
			}
		},
		// Установить нулевой вес для модуля (по имени/ключу)
		setNullWeightForModule(state, action: PayloadAction<string>) {
			const key = action.payload;
			for (const m of state.modulesConfig) {
				const name = m.moduleName ?? (m as any).module_name;
				if (!name) continue;
				if (name === key) m.moduleWeight = 0;
			}
		},
		// Сброс только вертикального деления (кнопка 2)
		resetSectionsOnly(state) {
			state.sectionsConfig = startSectionsConfig;
			state.workspaceSplitResetNonce += 1;
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem(
						"sectionsConfig",
						JSON.stringify(startSectionsConfig),
					);
				} catch (err) {
					console.warn("Failed to reset sectionsConfig in localStorage", err);
				}
			}
		},
		// Скрыть чат, дашборды на весь workspace (кнопка 3)
		showOnlyDashboard(state) {
			const cfg: sectionsConfigElem[] = [
				{ sectionName: "dashboard", sectionWeight: 1 },
				{ sectionName: "chat", sectionWeight: 0 },
			];
			state.sectionsConfig = cfg;
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem("sectionsConfig", JSON.stringify(cfg));
				} catch (err) {
					console.warn("Failed to save sectionsConfig in localStorage", err);
				}
			}
		},
		// Скрыть дашборды, чат на весь workspace (кнопка 4)
		showOnlyChat(state) {
			const cfg: sectionsConfigElem[] = [
				{ sectionName: "dashboard", sectionWeight: 0 },
				{ sectionName: "chat", sectionWeight: 1 },
			];
			state.sectionsConfig = cfg;
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem("sectionsConfig", JSON.stringify(cfg));
				} catch (err) {
					console.warn("Failed to save sectionsConfig in localStorage", err);
				}
			}
		},
		// Установить полную конфигурацию (кнопка 1 — после загрузки с сервера)
		setConfiguration(
			state,
			action: PayloadAction<{
				modulesConfig: modulesConfigElem[];
				sectionsConfig: sectionsConfigElem[];
			}>,
		) {
			const ordered = applyDefaultOrder(action.payload.modulesConfig);
			state.modulesConfig = ordered;
			state.sectionsConfig = action.payload.sectionsConfig;
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem("modulesConfig", JSON.stringify(ordered));
					localStorage.setItem(
						"sectionsConfig",
						JSON.stringify(action.payload.sectionsConfig),
					);
				} catch (err) {
					console.warn("Failed to save configs in localStorage", err);
				}
			}
		},
		setSocketStatus(
			state,
			action: PayloadAction<
				| "online"
				| "connecting"
				| "offline-reconnect"
				| "connection-error"
				| "auth-error"
			>,
		) {
			state.socketStatus = action.payload;
		},
	},
});

export const {
	setModulesConfiguration,
	resetConfiguration,
	bumpLayoutHardResetNonce,
	setNullWeightForModule,
	setIsSocketLoaded,
	resetSectionsOnly,
	showOnlyDashboard,
	showOnlyChat,
	setConfiguration,
	toggleHighlightedApp,
	clearHighlightedApps,
	setSocketStatus,
} = appSlice.actions;

export default appSlice.reducer;
